/**
 * Desk (docs/desk-ux.md) — the signed-in home overview, in the shared "soft cards" look. Money
 * (Real · USD and Paper · ATLAS balances), a glance at what's in play (→ Portfolio), what's
 * happening now (the Live & soon strip of Markets game cards) and what needs attention. Detail
 * lives on Portfolio, Markets and History.
 *
 *   desktop  Real · Paper · Performance  /  Needs attention  /  In play | Exposed plays  /  Live & soon
 *   mobile   balances → needs attention → exposed plays → in play → live & soon → performance
 *
 * Exposed plays (lib/plays.ts) shows only while a play is exposed (one side filled, the other
 * not); otherwise In play takes the full row. It replaced the Opportunities glance when that page
 * folded into Portfolio.
 *
 * A new user (steps left: a paper order, a connected venue, real-money trading) gets a Getting
 * started card in place of the empty Real and In play cards.
 *
 * The screen reads everything through a `DeskSource` so the dev preview (/dev/desk) can render it
 * from mock data; `Desk` below wires the live hooks. Nothing here places an order: a price in the
 * strip only OPENS the shared trade ticket, and ↻ (refresh balances) runs only on a click.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type {
  AccountBalances,
  BalancePartner,
  Contest,
  League,
  LiquidityPosition,
  PartnerLiquidity,
  Performance,
} from "@greenbook/client";
import {
  useAccountBalances,
  useCardLiquidity,
  useContests,
  useOrders,
  usePerformance as usePerformanceQ,
  usePortfolioLiquidity,
  useRefreshBalances,
} from "../lib/queries.js";
import { usePartnerLogos } from "../lib/logos.js";
import { SCOPES, useAccess, useCanTrade, useOmSession } from "../lib/omauth.js";
import { useTradingMode } from "../lib/venues.js";
import { useIsMobile } from "../lib/responsive.js";
import { fmtKickoff, fmtMoney } from "../lib/format.js";
import { exposedPlays, outcomeLabel, PLAY_LABEL, playsFromOpportunities, type Play } from "../lib/plays.js";
import { mmFocusHref } from "../lib/marketMaking.js";
import { isGame, matchupSep, startMs, teamLabel, teamsOf, type CardQuote, type PriceStatus } from "../lib/marketsBoard.js";
import {
  buildHoldings,
  combineValues,
  isLiveGame,
  liveFromState,
  NO_CONTEST,
  pendingValue,
  rowPrice,
  rowsValue,
  sortGames,
  waitingAmt,
  type Ccy,
  type GameHolding,
  type ValueSummary,
} from "../lib/portfolio.js";
import {
  attentionItems,
  dismissNudge,
  gettingStarted,
  inPlayTop,
  isConnectedVenue,
  liveAndSoon,
  markGettingStartedDone,
  monthWindow,
  perfSummary,
  readDismissed,
  readGettingStartedDone,
  readTried,
  roiText,
  showGettingStarted,
  signedAmount,
  type AttentionItem,
  type GettingStarted,
  type PerfSummary,
} from "../lib/desk.js";
import { liveSource as portfolioLiveSource, PORTFOLIO_EXPOSED_HREF, type PortfolioSource, type StateEntry } from "./Portfolio.js";
import { ErrorState } from "../components/ui.js";
import { SectionHeading, Skeleton, SoftCard, TicketDrawer } from "../components/kit.js";
import {
  CardPricesCtx,
  GameCard,
  GameCardSkeleton,
  ViewWatcher,
  WatcherCtx,
  type CardPricesHook,
} from "../components/GameCard.js";
import { VenueLogo } from "../components/VenueLogo.js";
import { TradeTicket } from "../components/TradeTicket.js";
import { showUnlock } from "../components/Gate.js";

/** Where venues are connected, reconnected and funded (greenbook never handles credentials). */
export const VENUES_URL = "https://app.openmarkets.ai";

/* =================================================================== source */

interface QueryState {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/** What the strip's ticket drawer is asked to open (the same contract as Markets). */
export interface DeskTicketPick {
  contestId: string;
  position: LiquidityPosition;
  pl: PartnerLiquidity;
}

/**
 * Everything the Desk reads. Each `use*` is a hook, called unconditionally in the same order on
 * every render; the object itself must be stable for the screen's lifetime.
 */
export interface DeskSource {
  /** The Portfolio hooks In play and the strip reuse (orders, contests, states, venue scope). */
  portfolio: Pick<
    PortfolioSource,
    "useOpen" | "useResting" | "useContests" | "useLeagues" | "useStates" | "useScope" | "useLiveGames" | "useOpenPlays" | "useMmIds"
  >;
  useBalances(): QueryState & { data?: AccountBalances };
  /** ↻ — re-read every venue's balance. Only ever run from a click. */
  useRefresh(): { run: () => void; isPending: boolean; isError: boolean; refreshed: number | null };
  usePerformance(from: string, to: string): QueryState & { total?: Performance; byDay?: Performance };
  /** Any paper (ATLAS) order ever; null while loading. */
  useHasPaperOrder(): boolean | null;
  /** The session's granted scopes; null when they can't be known (a pasted key). */
  useGrantedScopes(): readonly string[] | null;
  useCanTrade(): boolean;
  /** May live game state be read (gamestate:read)? Without it "live" is presumed from start times. */
  useGameStateAccess(): boolean;
  useLogos(): Map<string, string>;
  /** One held game's Value now (same maths as Portfolio). */
  useGameValue(game: GameHolding, contest: Contest | undefined, allowed: ReadonlySet<string> | null): ValueSummary;
  /** Open contests, for the Live & soon strip. */
  useBoard(now: number): QueryState & { contests: Contest[] };
  /** How a strip card reads its prices. */
  cardPrices: CardPricesHook;
  renderTicket(pick: DeskTicketPick, onClose: () => void): ReactNode;
  /** The per-browser lists (features tried, nudges dismissed, Getting started finished). */
  prefs: {
    readTried(): string[];
    readDismissed(): string[];
    dismiss(scope: string): string[];
    readGsDone(): boolean;
    markGsDone(): void;
  };
}

/* ------------------------------------------------------------- live source */

const BOARD_PAGE = 200;
const BOARD_MAX_PAGES = 5;
const SOON_WINDOW_MS = 3 * 60 * 60_000;

const liveDeskSource: DeskSource = {
  portfolio: portfolioLiveSource,
  useBalances() {
    const q = useAccountBalances();
    return { data: q.data, isLoading: q.isLoading, isError: q.isError, error: q.error, refetch: () => void q.refetch() };
  },
  useRefresh() {
    const m = useRefreshBalances();
    return {
      run: () => m.mutate(),
      isPending: m.isPending,
      isError: m.isError,
      refreshed: m.isSuccess ? (m.data?.data.refreshed_partner_ids?.length ?? 0) : null,
    };
  },
  usePerformance(from, to) {
    const total = usePerformanceQ({ groupBy: "none", from, to });
    const byDay = usePerformanceQ({ groupBy: "day", from, to });
    return {
      total: total.data,
      byDay: byDay.data,
      isLoading: total.isLoading,
      isError: total.isError,
      error: total.error,
      refetch: () => void total.refetch(),
    };
  },
  useHasPaperOrder() {
    const q = useOrders(1, { currency: "ATLAS" });
    if (q.isLoading) return null;
    if (q.isError) return false;
    return (q.data?.pages[0]?.data.length ?? 0) > 0;
  },
  useGrantedScopes() {
    return useOmSession()?.scopes ?? null;
  },
  useCanTrade() {
    return useCanTrade();
  },
  useGameStateAccess() {
    return useAccess(SCOPES.gamestate).ok;
  },
  useLogos() {
    return usePartnerLogos();
  },
  useGameValue(game, contest, allowed) {
    const id = game.contestId;
    const real = id !== NO_CONTEST;
    const asGame = contest ? isGame(contest) : true;
    const hashes = useMemo(() => game.rows.map((r) => r.positionHash).filter((h): h is string => !!h), [game.rows]);
    // Cheap first: the Markets card's trimmed board (a few KB). When it already holds every held
    // side (a moneyline-only holding) that's the price; otherwise fall back to the held sides'
    // liquidity, exactly as Portfolio reads it. Both share Portfolio's / Markets' cache.
    const card = useCardLiquidity(id, asGame, { enabled: real, poll: false });
    const covered =
      !!card.data && hashes.length > 0 && hashes.every((h) => card.data!.positions.some((p) => p.position_hash === h));
    const needFull = real && !covered && (!!card.data || card.isError);
    const full = usePortfolioLiquidity(id, asGame, hashes, { enabled: needFull, poll: false });
    const data = covered ? { positions: card.data!.positions as LiquidityPosition[] } : full.data;
    const failed = !real || (!covered && full.isError && !full.data);
    return useMemo(
      () => rowsValue(game.rows, (r) => (failed ? { state: "none" } : rowPrice(r, data, allowed))),
      [game.rows, data, failed, allowed],
    );
  },
  useBoard(now) {
    // The same list Markets loads (shared cache). Rows come oldest start first, and stale "open"
    // games can push today off page 1 (FRICTION.md), so page on until the window is covered.
    const q = useContests({ status: "open", limit: BOARD_PAGE });
    const contests = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.data), [q.data]);
    const lastStart = contests.length ? startMs(contests[contests.length - 1]) : NaN;
    const pages = q.data?.pages.length ?? 0;
    const { hasNextPage, isFetching, isError, fetchNextPage } = q;
    const covered = Number.isFinite(lastStart) && lastStart > now + SOON_WINDOW_MS;
    useEffect(() => {
      if (hasNextPage && !covered && !isFetching && !isError && pages > 0 && pages < BOARD_MAX_PAGES) {
        void fetchNextPage({ cancelRefetch: false });
      }
    }, [hasNextPage, covered, isFetching, isError, pages, fetchNextPage]);
    return { contests, isLoading: q.isLoading, isError: q.isError, error: q.error, refetch: () => void q.refetch() };
  },
  cardPrices: useCardLiquidity,
  renderTicket(pick, onClose) {
    return <LiveTicket pick={pick} onClose={onClose} />;
  },
  prefs: {
    readTried,
    readDismissed,
    dismiss: dismissNudge,
    readGsDone: readGettingStartedDone,
    markGsDone: markGettingStartedDone,
  },
};

function LiveTicket({ pick, onClose }: { pick: DeskTicketPick; onClose: () => void }) {
  const mode = useTradingMode();
  return (
    <TradeTicket
      key={`${pick.position.position_hash}|${pick.pl.partner_id}`}
      position={pick.position}
      pl={pick.pl}
      initialMode={mode}
      onClose={onClose}
    />
  );
}

/** The routed page (`/`, signed in with account:read). */
export function Desk() {
  return <DeskScreen source={liveDeskSource} />;
}

/* =================================================================== helpers */

const money = (n: number, ccy: Ccy): string => (ccy === "ATLAS" ? `${Math.round(n).toLocaleString()} ATLAS` : fmtMoney(n));
const toneOf = (n: number): string => (n > 0.005 ? "text-up" : n < -0.005 ? "text-down" : "text-dim");
const arrowOf = (n: number): string => (n > 0.005 ? "▲" : n < -0.005 ? "▼" : "");

/** Spendable balance across a currency class (expected balance when present; never mixes classes). */
function classTotal(data: AccountBalances | undefined, real: boolean): number {
  return (data?.by_currency ?? [])
    .filter((b) => (b.is_real_money ?? false) === real)
    .reduce((n, b) => {
      const ps = b.partners ?? [];
      if (ps.length) return n + ps.reduce((s, p) => s + (p.expected_balance ?? p.balance ?? 0), 0);
      return n + (b.total ?? 0);
    }, 0);
}
function realPartners(data: AccountBalances | undefined): BalancePartner[] {
  return (data?.by_currency ?? []).filter((b) => b.is_real_money).flatMap((b) => b.partners ?? []);
}

const timeOf = (iso: string | null | undefined): string | null => {
  const t = iso ? new Date(iso) : null;
  return t && !Number.isNaN(t.getTime()) ? t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;
};

const btnPrimary =
  "inline-flex min-h-11 shrink-0 items-center justify-center rounded-full border border-amber/60 bg-amber/15 px-4 text-[13px] font-semibold text-amber hover:bg-amber/25 lg:min-h-9";
const btnSecondary =
  "inline-flex min-h-11 shrink-0 items-center justify-center rounded-full border border-border-2 bg-panel px-4 text-[13px] text-ink hover:bg-raised lg:min-h-9";
const linkMore = "inline-flex min-h-11 items-center text-[12px] text-dim hover:text-amber lg:min-h-0";

/* =================================================================== screen */

export function DeskScreen({ source, nowOverride }: { source: DeskSource; nowOverride?: number }) {
  const isMobile = useIsMobile();
  const mode = useTradingMode();
  const ccy: Ccy = mode === "real" ? "USD" : "ATLAS";

  // Ticker: live/soon split, "in 20m".
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const now = nowOverride ?? tick;

  /* ------------------------------------------------------------- data */
  const balances = source.useBalances();
  const refresh = source.useRefresh();
  const { from, to } = monthWindow(new Date(now));
  const perf = source.usePerformance(from, to);
  const openPlays = source.portfolio.useOpenPlays();
  const mmIds = source.portfolio.useMmIds();
  const exposed = useMemo(() => exposedPlays(playsFromOpportunities(openPlays.items, { mmIds })), [openPlays.items, mmIds]);
  const hasPaperOrder = source.useHasPaperOrder();
  const granted = source.useGrantedScopes();
  const canTrade = source.useCanTrade();
  const logos = source.useLogos();
  const resting = source.portfolio.useResting();

  const partners = useMemo(() => realPartners(balances.data), [balances.data]);
  const connected = partners.filter(isConnectedVenue);
  const realTotal = classTotal(balances.data, true);
  const paperTotal = classTotal(balances.data, false);

  // Working orders are always real money (paper never rests).
  const working = useMemo(
    () => ({ count: resting.items.length, waiting: resting.items.reduce((n, o) => n + waitingAmt(o), 0) }),
    [resting.items],
  );

  /* ---------------------------------------------------- getting started */
  const [gsFinished, setGsFinished] = useState(source.prefs.readGsDone);
  const gs: GettingStarted | null = useMemo(
    () =>
      balances.isLoading || hasPaperOrder == null
        ? null
        : gettingStarted({ hasPaperOrder, hasVenue: connected.length > 0, canTrade }),
    [balances.isLoading, hasPaperOrder, connected.length, canTrade],
  );
  useEffect(() => {
    if (gs?.complete && !gsFinished) {
      source.prefs.markGsDone();
      setGsFinished(true);
    }
  }, [gs?.complete, gsFinished, source]);
  const showGs = showGettingStarted(gs, gsFinished);

  /* ---------------------------------------------------- needs attention */
  const [dismissed, setDismissed] = useState(source.prefs.readDismissed);
  // Re-read the tried list whenever the page renders after an explainer closed (cheap).
  const tried = source.prefs.readTried();
  const attention = useMemo(
    () =>
      attentionItems({
        venues: partners,
        working,
        granted,
        tried,
        dismissed,
        suppress: showGs ? [SCOPES.trade] : [],
        triedOnly: showGs,
      }),
    // `tried` is a fresh array each render; key it on its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partners, working, granted, tried.join(","), dismissed, showGs],
  );

  /* ------------------------------------------------------------ in play */
  const inPlay = useInPlay(source, ccy, now);

  /* -------------------------------------------------------------- ticket */
  const [ticket, setTicket] = useState<DeskTicketPick | null>(null);
  const closeTicket = useCallback(() => setTicket(null), []);

  /* ------------------------------------------------------------- layout */
  const hideReal = showGs && !balances.isLoading && connected.length === 0;
  const hideInPlay = showGs && inPlay.ready && inPlay.games.length === 0;
  const perfSummaryData = useMemo(() => perfSummary(perf.total, perf.byDay, ccy === "USD", new Date(now)), [perf.total, perf.byDay, ccy, now]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1">
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          {/* One grid at lg (desktop order = document order); a flex column below lg, re-ordered
              to the mobile priority with `order-*`. */}
          <div className="mx-auto flex max-w-6xl flex-col gap-3 p-4 lg:grid lg:grid-cols-3 lg:gap-4">
            {!hideReal && (
              <div className="order-1 flex min-w-0 flex-col [&>*]:flex-1 lg:order-none">
                <RealCard
                  loading={balances.isLoading}
                  error={balances.isError ? balances : null}
                  total={realTotal}
                  partners={partners}
                  connectedCount={connected.length}
                  logos={logos}
                  refresh={refresh}
                />
              </div>
            )}
            <div className="order-2 flex min-w-0 flex-col [&>*]:flex-1 lg:order-none">
              <PaperCard loading={balances.isLoading} total={paperTotal} />
            </div>
            <div className={`order-6 flex min-w-0 flex-col lg:order-none [&>*]:flex-1 ${hideReal ? "lg:col-span-2" : ""}`}>
              <PerformanceCard loading={perf.isLoading} error={perf.isError ? perf : null} summary={perfSummaryData} />
            </div>

            {attention.length > 0 && (
              <div className="order-3 min-w-0 lg:order-none lg:col-span-3">
                <AttentionCard
                  items={attention}
                  onDismiss={(scope) => setDismissed(source.prefs.dismiss(scope))}
                />
              </div>
            )}

            <div className={`order-4 flex min-w-0 flex-col gap-3 lg:order-none lg:gap-4 ${exposed.length > 0 ? "lg:col-span-2" : "lg:col-span-3"}`}>
              {showGs && gs && <GettingStartedCard gs={gs} />}
              {!hideInPlay && <InPlayCard data={inPlay} ccy={ccy} />}
            </div>
            {exposed.length > 0 && (
              <div className="order-3 flex min-w-0 flex-col lg:order-none [&>*]:flex-1">
                <ExposedPlaysCard plays={exposed} />
              </div>
            )}

            <div className="order-5 min-w-0 lg:order-none lg:col-span-3">
              <LiveSoonStrip source={source} now={now} ccy={ccy} activeTicket={ticket} onPick={setTicket} />
            </div>
          </div>
        </div>

        <TicketDrawer
          open={!!ticket}
          onClose={closeTicket}
          mobile={isMobile}
          header={<span className="text-[11px] font-semibold uppercase tracking-wider text-dim">Trade ticket</span>}
        >
          {ticket && source.renderTicket(ticket, closeTicket)}
        </TicketDrawer>
      </div>
    </div>
  );
}

/* ================================================================= balances */

function CardLabel({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex min-h-6 items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-dim">{children}</span>
      {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
    </div>
  );
}

function RealCard({
  loading,
  error,
  total,
  partners,
  connectedCount,
  logos,
  refresh,
}: {
  loading: boolean;
  error: QueryState | null;
  total: number;
  partners: BalancePartner[];
  connectedCount: number;
  logos: Map<string, string>;
  refresh: ReturnType<DeskSource["useRefresh"]>;
}) {
  const [open, setOpen] = useState(false);
  const status = refresh.isPending
    ? "Refreshing…"
    : refresh.isError
      ? "Refresh failed"
      : refresh.refreshed != null
        ? `Refreshed ${refresh.refreshed} venue${refresh.refreshed === 1 ? "" : "s"}`
        : null;
  return (
    <SoftCard className="h-full p-3 lg:p-4">
      <CardLabel
        right={
          <>
            {status && (
              <span className={`text-[11px] ${refresh.isError ? "text-down" : "text-faint"}`} aria-live="polite">
                {status}
              </span>
            )}
            <button
              type="button"
              onClick={refresh.run}
              disabled={refresh.isPending || loading}
              aria-label="Refresh balances"
              title="Re-read live balances from every connected venue (slow, 1–3 s)"
              className="relative z-10 -my-2 -mr-2 grid h-11 w-11 place-items-center rounded-full text-[16px] text-dim hover:bg-raised hover:text-ink disabled:opacity-50 lg:h-8 lg:w-8"
            >
              <span className={refresh.isPending ? "inline-block motion-safe:animate-spin" : ""} aria-hidden>
                ↻
              </span>
            </button>
          </>
        }
      >
        Real · USD
      </CardLabel>
      {loading ? (
        <>
          <Skeleton className="mt-2 h-8 w-36 max-w-full" />
          <Skeleton className="mt-2 h-3 w-24" />
        </>
      ) : error ? (
        <div className="mt-2">
          <ErrorState error={error.error} onRetry={error.refetch} />
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="desk-real-venues"
            className="-mx-1 mt-1 flex w-[calc(100%+0.5rem)] min-h-11 items-end gap-3 rounded-md px-1 text-left hover:bg-raised/40"
          >
            <span className="min-w-0 flex-1">
              <span className="nums block break-words text-2xl font-semibold leading-tight text-up lg:text-[28px]">
                {fmtMoney(total)}
              </span>
              <span className="mt-1 block text-[12px] text-dim">
                across {connectedCount} venue{connectedCount === 1 ? "" : "s"}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1 pb-0.5 text-[12px] text-faint">
              {open ? "Hide" : "Venues"}
              <span className={`transition-transform duration-150 motion-reduce:transition-none ${open ? "rotate-90" : ""}`} aria-hidden>
                ▸
              </span>
            </span>
          </button>
          {open && (
            <ul id="desk-real-venues" className="mt-3 flex flex-col gap-1.5 border-t border-border pt-3">
              {partners.length === 0 ? (
                <li className="text-[12px] text-faint">
                  No venues connected.{" "}
                  <a href={VENUES_URL} target="_blank" rel="noreferrer" className="text-amber hover:underline">
                    Connect one on OpenMarkets ↗
                  </a>
                </li>
              ) : (
                partners.map((p) => <VenueRow key={p.partner_id} partner={p} logo={logos.get(p.partner_id)} />)
              )}
            </ul>
          )}
        </>
      )}
    </SoftCard>
  );
}

function VenueRow({ partner: p, logo }: { partner: BalancePartner; logo?: string }) {
  const conn = isConnectedVenue(p);
  const off = (p.status ?? "active").toLowerCase() !== "active";
  const stale = conn && p.is_stale && p.balance_source !== "om_wallet";
  const state = off ? "Off" : !conn ? "Needs reconnecting" : stale ? (timeOf(p.balance_as_of) ? `as of ${timeOf(p.balance_as_of)}` : "Balance not read yet") : "Connected";
  const tone = off ? "text-faint" : !conn || stale ? "text-amber" : "text-dim";
  return (
    <li className="flex min-h-9 items-center gap-2.5">
      <VenueLogo name={p.partner_name ?? p.partner_id} logoUrl={logo} size={20} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] text-ink">{p.partner_name ?? p.partner_id}</div>
        <div className={`text-[11px] ${tone}`}>{state}</div>
      </div>
      <span className={`nums shrink-0 text-[13px] ${off ? "text-faint" : "text-ink"}`}>{fmtMoney(p.expected_balance ?? p.balance ?? 0)}</span>
    </li>
  );
}

function PaperCard({ loading, total }: { loading: boolean; total: number }) {
  return (
    <SoftCard className="h-full p-3 lg:p-4">
      <CardLabel>Paper · ATLAS</CardLabel>
      {loading ? (
        <>
          <Skeleton className="mt-2 h-8 w-32 max-w-full" />
          <Skeleton className="mt-2 h-3 w-24" />
        </>
      ) : (
        <div className="mt-1 min-h-11">
          <div className="nums break-words text-2xl font-semibold leading-tight text-blue lg:text-[28px]">
            {Math.round(total).toLocaleString()} <span className="text-[15px] font-medium">ATLAS</span>
          </div>
          <div className="mt-1 text-[12px] text-dim">practice wallet</div>
        </div>
      )}
    </SoftCard>
  );
}

/* ============================================================== performance */

function PerformanceCard({
  loading,
  error,
  summary,
}: {
  loading: boolean;
  error: QueryState | null;
  summary: PerfSummary;
}) {
  const month = new Date().toLocaleDateString(undefined, { month: "long" });
  const href = summary.currency === "ATLAS" ? "/history?book=paper" : "/history";
  return (
    <SoftCard href={href} linkLabel="Open History" className="h-full p-3 lg:p-4">
      <div className="pointer-events-none relative">
        <CardLabel right={<span className="text-[12px] text-faint">History →</span>}>
          Performance · {summary.currency === "USD" ? "Real" : "Paper"}
        </CardLabel>
        {loading ? (
          <>
            <Skeleton className="mt-2 h-6 w-48 max-w-full" />
            <Skeleton className="mt-2 h-6 w-full" />
          </>
        ) : error ? (
          <div className="mt-2 text-[12px] text-faint">Performance unavailable right now.</div>
        ) : (
          <>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className={`nums text-xl font-semibold leading-tight lg:text-2xl ${toneOf(summary.net)}`}>
                {signedAmount(summary.net, summary.currency)}
              </span>
              <span className="text-[12px] text-dim">
                this month · ROI <span className="nums">{roiText(summary.roi)}</span>
              </span>
            </div>
            {summary.settled === 0 ? (
              <div className="mt-2 text-[12px] text-faint">Nothing settled yet this month.</div>
            ) : summary.hasDays ? (
              <Sparkbars days={summary.days} ccy={summary.currency} label={`Daily net P&L, ${month}`} />
            ) : (
              <div className="mt-2 text-[11px] text-faint">{summary.settled} settled this month</div>
            )}
          </>
        )}
      </div>
    </SoftCard>
  );
}

/**
 * The month's daily net P&L as tiny bars around a zero line (green up, red down, a hairline for
 * a day with nothing settled). Decorative inside a link: the figures above say it in words.
 */
function Sparkbars({ days, ccy, label }: { days: { key: string; net: number }[]; ccy: Ccy; label: string }) {
  const H = 28;
  const mid = H / 2;
  const step = 5;
  const w = 3;
  const max = Math.max(...days.map((d) => Math.abs(d.net)), 0);
  const W = Math.max(days.length * step - (step - w), 1);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      className="mt-2 block max-w-full"
      role="img"
      aria-label={`${label}: best day ${signedAmount(Math.max(0, ...days.map((d) => d.net)), ccy)}`}
      preserveAspectRatio="none"
    >
      <line x1={0} x2={W} y1={mid} y2={mid} className="stroke-border-2" strokeWidth={1} />
      {days.map((d, i) => {
        const h = max > 0 ? Math.max(1.5, (Math.abs(d.net) / max) * (mid - 1)) : 0;
        if (Math.abs(d.net) < 0.005 || h === 0) return null;
        const up = d.net > 0;
        return (
          <rect
            key={d.key}
            x={i * step}
            y={up ? mid - h : mid}
            width={w}
            height={h}
            rx={1}
            className={up ? "fill-up" : "fill-down"}
          >
            <title>{`${d.key}: ${signedAmount(d.net, ccy)}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

/* ========================================================== needs attention */

function AttentionCard({ items, onDismiss }: { items: AttentionItem[]; onDismiss: (scope: string) => void }) {
  return (
    <section aria-label="Needs attention" className="rounded-lg border border-amber/30 bg-amber/[0.04] p-3 lg:p-4">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-amber">Needs attention</h2>
        <span className="nums text-[11px] text-faint">{items.length}</span>
      </div>
      <ul className="flex flex-col gap-2">
        {items.map((it) => (
          <AttentionRow key={it.id} item={it} onDismiss={onDismiss} />
        ))}
      </ul>
    </section>
  );
}

function AttentionRow({ item, onDismiss }: { item: AttentionItem; onDismiss: (scope: string) => void }) {
  let dot = "bg-amber";
  let title: ReactNode;
  let sub: ReactNode = null;
  let action: ReactNode;
  if (item.kind === "venue") {
    const at = timeOf(item.asOf);
    title = item.problem === "reconnect" ? `${item.venue} needs reconnecting` : `${item.venue}’s balance may be out of date`;
    sub =
      item.problem === "reconnect"
        ? "Its credentials on OpenMarkets aren’t connected, so real orders there will fail."
        : at
          ? `Last read at ${at}.`
          : "No fresh balance has been read from this venue yet.";
    action = (
      <a href={VENUES_URL} target="_blank" rel="noreferrer" className={btnSecondary}>
        Fix on OpenMarkets ↗
      </a>
    );
  } else if (item.kind === "working") {
    title = (
      <>
        <span className="nums">{item.count}</span> order{item.count === 1 ? "" : "s"} ·{" "}
        <span className="nums">{fmtMoney(item.waiting)}</span> waiting at venues
      </>
    );
    sub = "Resting until they fill or you cancel them.";
    action = (
      <Link to="/portfolio#working" className={btnSecondary}>
        View working →
      </Link>
    );
  } else {
    dot = "bg-blue";
    title = (
      <>
        {item.title}
        {item.tried && <span className="ml-2 rounded-full bg-blue/10 px-2 py-0.5 text-[10px] font-medium text-blue">you tried this</span>}
      </>
    );
    sub = `${item.body} Not included in this session’s access.`;
    action = (
      <span className="flex shrink-0 items-center gap-1">
        <button type="button" onClick={() => showUnlock(item.scope)} className={btnPrimary}>
          Unlock
        </button>
        <button
          type="button"
          onClick={() => onDismiss(item.scope)}
          aria-label={`Dismiss ${item.title}`}
          title="Don’t show this again (this browser)"
          className="grid h-11 w-11 place-items-center rounded-full text-faint hover:bg-raised hover:text-ink lg:h-9 lg:w-9"
        >
          ✕
        </button>
      </span>
    );
  }
  return (
    <li className="flex flex-col gap-2 rounded-md border border-border bg-panel-2 p-3 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex min-w-0 flex-1 items-start gap-2.5">
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden />
        <div className="min-w-0">
          <div className="break-words text-[13px] text-ink">{title}</div>
          {sub && <div className="mt-0.5 text-[12px] text-dim">{sub}</div>}
        </div>
      </div>
      <div className="flex justify-end pl-[18px] sm:pl-0">{action}</div>
    </li>
  );
}

/* ========================================================== getting started */

function GettingStartedCard({ gs }: { gs: GettingStarted }) {
  return (
    <SoftCard className="p-3 lg:p-4">
      <CardLabel right={<span className="nums text-[12px] text-faint">{gs.doneCount} of {gs.steps.length} done</span>}>
        Getting started
      </CardLabel>
      <ol className="mt-3 flex flex-col gap-2">
        {gs.steps.map((s, i) => (
          <li key={s.id} className="flex flex-col gap-2 rounded-md border border-border bg-panel p-3 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <span
                className={`nums grid h-6 w-6 shrink-0 place-items-center rounded-full border text-[12px] ${
                  s.done ? "border-up/50 bg-up/15 text-up" : "border-border-2 text-dim"
                }`}
                aria-hidden
              >
                {s.done ? "✓" : i + 1}
              </span>
              <div className="min-w-0">
                <div className={`text-[14px] ${s.done ? "text-dim line-through decoration-faint" : "text-ink"}`}>{s.label}</div>
                <div className="mt-0.5 text-[12px] text-faint">{s.hint}</div>
              </div>
              <span className="sr-only">{s.done ? "done" : "not done"}</span>
            </div>
            {!s.done && (
              <div className="flex justify-end pl-9 sm:pl-0">
                {s.id === "paper" ? (
                  <Link to="/markets" className={btnPrimary}>
                    Browse markets →
                  </Link>
                ) : s.id === "venue" ? (
                  <a href={VENUES_URL} target="_blank" rel="noreferrer" className={btnSecondary}>
                    Connect on OpenMarkets ↗
                  </a>
                ) : (
                  <button type="button" onClick={() => showUnlock(SCOPES.trade)} className={btnSecondary}>
                    Unlock real money
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>
    </SoftCard>
  );
}

/* ================================================================== in play */

interface InPlayGame {
  contestId: string;
  contest: Contest | undefined;
  league: League | undefined;
  state: StateEntry | undefined;
  live: boolean;
  stake: number;
  payout: number;
}
interface InPlayData extends QueryState {
  ready: boolean;
  atRisk: number;
  couldWin: number;
  openCount: number;
  value: ReturnType<typeof combineValues>;
  games: GameHolding[];
  top: InPlayGame[];
  probes: ReactNode;
}

/**
 * At risk · Value now · Could win and the next games held — the same derivation as Portfolio
 * (lib/portfolio.ts: holdings, game order, Value now), over the book the trading mode is on.
 */
function useInPlay(source: DeskSource, ccy: Ccy, now: number): InPlayData {
  const p = source.portfolio;
  const open = p.useOpen(ccy);
  const scope = p.useScope(ccy);
  const leagueOf = p.useLeagues();
  const holdings = useMemo(() => buildHoldings(open.items), [open.items]);
  const heldIds = useMemo(() => holdings.map((g) => g.contestId).filter((id) => id !== NO_CONTEST), [holdings]);
  const contestOf = p.useContests(heldIds);
  const stateOf = p.useStates(heldIds);
  const liveOf = useCallback((id: string) => liveFromState(stateOf(id)), [stateOf]);
  const games = useMemo(() => sortGames(holdings, contestOf, liveOf, now), [holdings, contestOf, liveOf, now]);

  const [values, setValues] = useState<Map<string, ValueSummary>>(() => new Map());
  const onValue = useCallback((id: string, v: ValueSummary) => {
    setValues((m) => {
      const o = m.get(id);
      if (o && o.value === v.value && o.cost === v.cost && o.priced === v.priced && o.total === v.total && o.pending === v.pending) return m;
      return new Map(m).set(id, v);
    });
  }, []);
  const value = useMemo(
    () => combineValues(games.map((g) => values.get(g.contestId) ?? pendingValue(g.rows.length))),
    [games, values],
  );

  const isLive = useCallback(
    (g: GameHolding) => isLiveGame(contestOf(g.contestId), liveOf(g.contestId), now),
    [contestOf, liveOf, now],
  );
  const top: InPlayGame[] = inPlayTop(games, contestOf, isLive, now).map((g) => {
    const contest = contestOf(g.contestId);
    const state = stateOf(g.contestId);
    return {
      contestId: g.contestId,
      contest,
      league: leagueOf(contest?.league_id ?? undefined),
      state,
      live: isLive(g),
      stake: g.stake,
      payout: g.payout,
    };
  });

  const probes = games.map((g) => (
    <ValueProbe key={g.contestId} source={source} game={g} contest={contestOf(g.contestId)} allowed={scope.allowed} onValue={onValue} />
  ));

  return {
    ready: !open.isLoading,
    isLoading: open.isLoading,
    isError: open.isError,
    error: open.error,
    refetch: open.refetch,
    atRisk: holdings.reduce((n, g) => n + g.stake, 0),
    couldWin: holdings.reduce((n, g) => n + g.payout, 0),
    openCount: holdings.reduce((n, g) => n + g.rows.length, 0),
    value,
    games,
    top,
    probes,
  };
}

/** Reads one held game's Value now and reports it up (renders nothing). */
function ValueProbe({
  source,
  game,
  contest,
  allowed,
  onValue,
}: {
  source: DeskSource;
  game: GameHolding;
  contest: Contest | undefined;
  allowed: ReadonlySet<string> | null;
  onValue: (id: string, v: ValueSummary) => void;
}) {
  const v = source.useGameValue(game, contest, allowed);
  useEffect(() => onValue(game.contestId, v), [game.contestId, v, onValue]);
  return null;
}

function InPlayCard({ data, ccy }: { data: InPlayData; ccy: Ccy }) {
  const v = data.value;
  return (
    <SoftCard className="p-3 lg:p-4">
      {data.probes}
      <CardLabel
        right={
          <Link to="/portfolio" className={`${linkMore} relative z-10`}>
            Portfolio →
          </Link>
        }
      >
        In play · {ccy === "USD" ? "Real" : "Paper"}
      </CardLabel>
      {data.isError ? (
        <div className="mt-2">
          <ErrorState error={data.error} onRetry={data.refetch} />
        </div>
      ) : !data.ready ? (
        <div aria-busy="true">
          <div className="mt-2 grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <Skeleton className="h-3 w-14" />
                <Skeleton className="mt-1.5 h-6 w-20 max-w-full" />
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-col gap-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full rounded-md" />
            ))}
          </div>
        </div>
      ) : data.games.length === 0 ? (
        <div className="mt-2 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <p className="flex-1 text-[13px] text-dim">
            {ccy === "USD" ? "No real-money positions open." : "No paper positions open — play money, every venue."}
          </p>
          <Link to="/markets" className={btnSecondary}>
            Browse markets →
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <Metric label="At risk" value={money(data.atRisk, ccy)} sub={`${data.openCount} open`} />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-faint">Value now</div>
              {v.priced === 0 && v.total > 0 ? (
                <>
                  <Skeleton className="mt-1.5 h-5 w-20 max-w-full" />
                  <div className="mt-1 text-[11px] text-faint">pricing…</div>
                </>
              ) : (
                <>
                  <div className="nums mt-0.5 break-words text-[17px] font-semibold leading-tight text-ink lg:text-xl">{money(v.value, ccy)}</div>
                  <div className={`nums mt-0.5 text-[11px] ${v.partial ? "text-faint" : toneOf(v.change)}`}>
                    {v.partial ? `${v.priced} of ${v.total} priced` : `${arrowOf(v.change)} ${signedAmount(v.change, ccy)}`.trim()}
                  </div>
                </>
              )}
            </div>
            <Metric label="Could win" value={money(data.couldWin, ccy)} sub="if all win" tone="up" />
          </div>
          {data.top.length > 0 ? (
            <ul className="mt-3 flex flex-col gap-1.5">
              {data.top.map((g) => (
                <InPlayRow key={g.contestId} game={g} ccy={ccy} />
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-[12px] text-faint">Nothing you hold is live or still to start.</p>
          )}
          {data.games.length > data.top.length && (
            <div className="mt-1 text-right text-[11px] text-faint">
              {data.top.length === 0
                ? `${data.games.length} game${data.games.length === 1 ? "" : "s"} held on Portfolio`
                : `+${data.games.length - data.top.length} more game${data.games.length - data.top.length === 1 ? "" : "s"} on Portfolio`}
            </div>
          )}
        </>
      )}
    </SoftCard>
  );
}

function Metric({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "up" }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-faint">{label}</div>
      <div className={`nums mt-0.5 break-words text-[17px] font-semibold leading-tight lg:text-xl ${tone === "up" ? "text-up" : "text-ink"}`}>{value}</div>
      <div className="mt-0.5 text-[11px] text-dim">{sub}</div>
    </div>
  );
}

function scoreText(st: StateEntry | undefined): string | null {
  const s = st?.state?.scores;
  if (!s) return null;
  const home = s.home ?? s.home_score;
  const away = s.away ?? s.away_score;
  return home == null && away == null ? null : `${away ?? "–"} – ${home ?? "–"}`;
}

function InPlayRow({ game: g, ccy }: { game: InPlayGame; ccy: Ccy }) {
  const c = g.contest;
  const teams = c ? teamsOf(c) : null;
  const title = g.contestId === NO_CONTEST ? "Unattributed orders" : c ? (teams ? `${teamLabel(teams.away)} ${matchupSep(c)} ${teamLabel(teams.home)}` : (c.title ?? "—")) : null;
  const clock = [g.state?.state?.period?.label, g.state?.state?.clock?.display].filter(Boolean).join(" · ");
  const when = g.live ? clock || "Live" : c && isGame(c) ? fmtKickoff(c.starts_at) : "Futures";
  const score = g.live ? scoreText(g.state) : null;
  const href = g.contestId !== NO_CONTEST ? `/contests/${encodeURIComponent(g.contestId)}` : "/portfolio";
  return (
    <li>
      <Link
        to={href}
        className={`relative z-10 flex min-h-11 items-center gap-3 rounded-md border px-3 py-2 hover:border-border-2 ${g.live ? "border-blue/30" : "border-border"} bg-panel`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-faint">
            {g.live && <span className="pulse inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue" aria-hidden />}
            <span className="shrink-0 font-medium uppercase tracking-wide text-dim">{g.league?.name ?? ""}</span>
            <span className={`nums min-w-0 truncate ${g.live ? "text-blue" : ""}`}>{when}</span>
          </div>
          {title ? (
            <div className="mt-0.5 truncate text-[13px] text-ink">
              {title}
              {score && <span className="nums ml-2 font-semibold text-blue">{score}</span>}
            </div>
          ) : (
            <Skeleton className="mt-1 h-4 w-40 max-w-full" />
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="nums text-[13px] text-ink">{money(g.stake, ccy)}</div>
          <div className="nums text-[11px] text-up">→ {money(g.payout, ccy)}</div>
        </div>
      </Link>
    </li>
  );
}

/* ============================================================ exposed plays */

/** Exposed plays (one side filled, the other not): up to 3, each with its payout per outcome. */
function ExposedPlaysCard({ plays }: { plays: Play[] }) {
  const shown = plays.slice(0, 3);
  return (
    <SoftCard className="h-full border-down/40 p-3 lg:p-4">
      <CardLabel
        right={
          <Link to={PORTFOLIO_EXPOSED_HREF} className={linkMore}>
            {plays.length > shown.length ? `All ${plays.length} →` : "Portfolio →"}
          </Link>
        }
      >
        <span className="text-down">Exposed plays · {plays.length}</span>
      </CardLabel>
      <ul className="mt-2 flex flex-col gap-1.5">
        {shown.map((p) => {
          const ccy = p.ccy === "ATLAS" ? "ATLAS" : "USD";
          const names = (p.outcomes ?? []).filter((o) => o.key !== "otherwise").map((o) => o.label.replace(/ win$/, ""));
          return (
            <li key={p.id}>
              <Link
                to={p.kind === "mm" ? mmFocusHref(p.id) : PORTFOLIO_EXPOSED_HREF}
                className="flex min-h-11 flex-col justify-center gap-0.5 rounded-md border border-down/30 bg-down/[0.06] px-3 py-2 hover:border-down/60"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="shrink-0 rounded-full border border-down/50 bg-down/15 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-down">
                    {PLAY_LABEL[p.kind]}
                  </span>
                  <span className="min-w-0 truncate text-[13px] text-ink">{names.join(" / ") || outcomeLabel(p.legs[0]?.title)}</span>
                </span>
                {p.outcomes && (
                  <span className="nums flex flex-wrap gap-x-2 text-[11px]">
                    {p.outcomes.map((o) => (
                      <span key={o.key} className="whitespace-nowrap">
                        <span className="text-faint">if {o.label}</span>{" "}
                        <span className={o.payout > 0.005 ? "text-up" : o.payout < -0.005 ? "text-down" : "text-dim"}>{signedAmount(o.payout, ccy)}</span>
                      </span>
                    ))}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </SoftCard>
  );
}

/* ============================================================= live & soon */

/** Batch-read live state only for games that could plausibly be live or about to start. */
const STATE_BEFORE_MS = 12 * 3_600_000;
const STATE_AFTER_MS = 3 * 3_600_000;

function LiveSoonStrip({
  source,
  now,
  ccy,
  activeTicket,
  onPick,
}: {
  source: DeskSource;
  now: number;
  ccy: Ccy;
  activeTicket: DeskTicketPick | null;
  onPick: (p: DeskTicketPick) => void;
}) {
  const board = source.useBoard(now);
  const stateAccess = source.useGameStateAccess();
  const leagueOf = source.portfolio.useLeagues();
  // Real mode: best price among the connected venues (null = any), the same scope as Markets.
  const scope = source.portfolio.useScope(ccy);

  const candidateIds = useMemo(
    () =>
      board.contests
        .filter((c) => {
          if (!isGame(c)) return false;
          const s = startMs(c);
          return Number.isFinite(s) && s >= now - STATE_BEFORE_MS && s <= now + STATE_AFTER_MS;
        })
        .map((c) => c.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [board.contests, Math.floor(now / 300_000)],
  );
  const stateOf = source.portfolio.useStates(candidateIds);

  const [statuses, setStatuses] = useState<Map<string, PriceStatus>>(() => new Map());
  const onStatus = useCallback((id: string, s: PriceStatus) => {
    setStatuses((m) => (m.get(id) === s ? m : new Map(m).set(id, s)));
  }, []);
  const cards = useMemo(
    () =>
      liveAndSoon(
        board.contests,
        (c) => (stateAccess ? liveFromState(stateOf(c.id)) : null),
        (c) => statuses.get(c.id) ?? "pending",
        now,
      ),
    [board.contests, stateAccess, stateOf, statuses, now],
  );

  const ids = useMemo(() => cards.map((c) => c.contest.id), [cards]);
  const live = source.portfolio.useLiveGames(ids);
  const streamed = useMemo(() => new Set(live ? ids : []), [live, ids]);
  const onVisible = useCallback(() => {}, []);

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  // Rooted on the strip itself; the near box reaches a few cards past either edge.
  const watcher = useMemo(() => (scrollEl ? new ViewWatcher(scrollEl, "0px 600px 0px 600px") : null), [scrollEl]);
  useEffect(() => () => watcher?.disconnect(), [watcher]);

  const pick = useCallback(
    (contest: Contest, q: CardQuote) => {
      if (!q.best) return;
      onPick({ contestId: contest.id, position: q.position as LiquidityPosition, pl: q.best as PartnerLiquidity });
    },
    [onPick],
  );

  const anyLive = cards.some((c) => c.bucket === "live");
  const allMarkets = (
    <Link to="/markets" className={linkMore}>
      All markets →
    </Link>
  );
  const slot = "w-[min(20rem,calc(100vw-4rem))] shrink-0 snap-start";

  return (
    <section aria-label="Live and starting soon">
      <SectionHeading title="Live & soon" tone={anyLive ? "live" : "default"} count={cards.length || undefined} right={allMarkets} className="!pt-0 pb-2" />
      {board.isError ? (
        <ErrorState error={board.error} onRetry={board.refetch} />
      ) : board.isLoading ? (
        <div className="-mx-4 flex gap-3 overflow-hidden px-4" aria-busy="true" aria-label="Loading games">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={slot}>
              <GameCardSkeleton />
            </div>
          ))}
        </div>
      ) : cards.length === 0 ? (
        <SoftCard className="flex flex-col items-start gap-3 p-4 sm:flex-row sm:items-center">
          <p className="flex-1 text-[13px] text-dim">Nothing live or starting in the next 3 hours.</p>
          <Link to="/markets" className={btnSecondary}>
            Browse markets →
          </Link>
        </SoftCard>
      ) : (
        <CardPricesCtx.Provider value={source.cardPrices}>
          <WatcherCtx.Provider value={watcher}>
            <div
              ref={setScrollEl}
              className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto overscroll-x-contain px-4 pb-2 lg:mx-0 lg:scroll-px-0 lg:px-0"
              role="list"
            >
              {cards.map(({ contest, bucket }) => (
                <div key={contest.id} className={slot} role="listitem">
                  <GameCard
                    contest={contest}
                    bucket={bucket}
                    league={leagueOf(contest.league_id ?? undefined)}
                    state={stateOf(contest.id) as StateEntry | undefined}
                    now={now}
                    allowed={scope.allowed}
                    tradeableOnly
                    streamed={streamed.has(contest.id)}
                    activeHash={activeTicket?.contestId === contest.id ? activeTicket.position.position_hash : null}
                    onPick={pick}
                    onStatus={onStatus}
                    onVisible={onVisible}
                  />
                </div>
              ))}
              <div className="flex w-40 shrink-0 snap-start items-center justify-center" role="listitem">
                <Link
                  to="/markets"
                  className="flex min-h-11 w-full items-center justify-center rounded-lg border border-dashed border-border-2 px-4 py-6 text-[13px] text-dim hover:border-amber/40 hover:text-amber"
                >
                  All markets →
                </Link>
              </div>
            </div>
          </WatcherCtx.Provider>
        </CardPricesCtx.Provider>
      )}
    </section>
  );
}
